import React, { useState, useEffect, useReducer } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

const FETCH_INIT = 'FETCH_INIT';
const FETCH_SUCCESS = 'FETCH_SUCCESS';
const FETCH_FAILURE = 'FETCH_FAILURE';

const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case FETCH_INIT:
      return {
        ...state,
        loaded: false,
        isLoading: true,
        isError: false,
      };
    case FETCH_SUCCESS:
      return {
        ...state,
        loaded: true,
        isLoading: false,
        isError: false,
        data: action.payload,
      };
    case FETCH_FAILURE:
      return {
        ...state,
        loaded: false,
        isLoading: false,
        isError: true,
      };
    default:
      throw new Error();
  }
};

function useDataApi(initialRequest, initialData, onError, onSuccess) {
  const [request, setRequest] = useState(initialRequest);
  const [t] = useTranslation();
  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: false,
    isError: false,
    loaded: false,
    data: initialData,
  });

  const handleSuccessResponse = (result, didCancel) => {
    if (didCancel) return;
    
    const data = result?.data ?? null;
    dispatch({
      type: FETCH_SUCCESS,
      payload: data,
    });
    
    if (onSuccess) {
      return data ? onSuccess(data) : onSuccess();
    }
  };

  const getErrorMessage = (error) => {
    let errorMessage = 'Server error';
    
    if (error.response?.data) {
      errorMessage = error.response.data.detailMessage || 
                    error.response.data.message || 
                    errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return errorMessage;
  };

  const getAuthErrorMessage = (error) => {
    const authErrorMap = {
      'auth/wrong-password': t('loginForm.wrong-password'),
      'auth/invalid-email': t('loginForm.invalid-email'),
    };

    return authErrorMap[error.code] || `Error: ${error.message}`;
  };

  const handleErrorResponse = (error, didCancel) => {
    if (didCancel) return;
    
    let errorMessage = getErrorMessage(error);
    
    if (error.code) {
      errorMessage = getAuthErrorMessage(error);
    }

    if (onError) {
      onError(errorMessage);
    }
    
    dispatch({ type: FETCH_FAILURE });
  };

  useEffect(() => {
    let didCancel = false;
    
    const fetchData = async () => {
      dispatch({ type: FETCH_INIT });
      
      try {
        const result = await request;
        handleSuccessResponse(result, didCancel);
      } catch (error) {
        handleErrorResponse(error, didCancel);
      }
      
      return didCancel;
    };

    if (typeof request !== 'boolean') {
      fetchData();
    }

    return () => {
      didCancel = true;
    };
  }, [request]);

  return [state, setRequest];
}

export { useDataApi };
